'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslations, useLocale } from 'next-intl'
import { formatWaitTime } from '@/lib/utils/format-wait'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { forgotPassword } from '@/app/auth/actions'
import { toast } from 'sonner'
import { Loader2, ArrowLeft } from 'lucide-react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { LocaleSwitcher } from '@/components/locale-switcher'

const COOLDOWN_SECONDS = 10

export function ForgotPasswordForm() {
  const t = useTranslations('auth')
  const currentLocale = useLocale()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const cooldownRef = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useSearchParams()

  const startCooldown = useCallback((seconds: number) => {
    setCooldown(seconds)
    if (cooldownRef.current) clearInterval(cooldownRef.current)
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [])

  useEffect(() => {
    // The fixed title only: the raw `?error=` text is never echoed (ALU-518).
    if (searchParams.get('error')) {
      toast.error(t('toast.resetLinkFailed'))
    }
  }, [searchParams, t])

  const handleSubmit = async (formData: FormData) => {
    if (cooldown > 0) return

    setIsLoading(true)
    try {
      const result = await forgotPassword(formData)

      if (result?.error) {
        const retryAfter =
          'retryAfter' in result && typeof result.retryAfter === 'number'
            ? result.retryAfter
            : COOLDOWN_SECONDS
        startCooldown(retryAfter)
        toast.error(t('toast.requestFailed'), {
          description: result.error,
        })
      } else {
        setIsSubmitted(true)
        toast.success(t('toast.emailSent'), {
          description: t('toast.checkInboxForResetLink'),
        })
      }
    } catch {
      toast.error(t('toast.somethingWentWrong'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white md:bg-[#fdfbf7] md:p-4">
      <div className="w-full flex flex-col flex-1 md:flex-none max-w-md bg-white md:border md:border-border/50 md:shadow-sm md:rounded-md px-6 pt-[calc(env(safe-area-inset-top)+2rem)] md:pt-8 pb-6 md:p-8 animate-in fade-in zoom-in-95 duration-300 relative">
        <div className="absolute top-3 right-3 md:top-4 md:right-4 z-10">
          <LocaleSwitcher currentLocale={currentLocale} />
        </div>
        <div className="text-center mb-6 md:mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-2">
            {t('recovery')}
          </h1>
          <p className="text-muted-foreground">{t('restoreAccess')}</p>
        </div>

        {isSubmitted ? (
          <div className="text-center space-y-6">
            <div className="bg-green-50 text-green-800 p-4 rounded-md text-sm">
              {t('resetLinkSent')}
            </div>
            <Button asChild variant="outline" className="w-full h-14 md:h-10 text-base md:text-sm">
              <Link href="/login">{t('returnToLogin')}</Link>
            </Button>
          </div>
        ) : (
          <form className="space-y-5 md:space-y-4" action={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-foreground mb-1.5 md:mb-1"
              >
                {t('emailAddress')}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="colleague@university.edu"
                className="bg-white md:bg-[#fdfbf7] h-12 md:h-10 text-base md:text-sm"
                inputMode="email"
                enterKeyHint="done"
                disabled={isLoading}
                defaultValue={searchParams.get('email') ?? ''}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || cooldown > 0}
              className="w-full font-bold text-base h-14 md:h-10 py-0 mt-4"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('sending')}
                </>
              ) : cooldown > 0 ? (
                t('pleaseWait', { wait: formatWaitTime(cooldown, t) })
              ) : (
                t('sendResetLink')
              )}
            </Button>

            <div className="mt-6 text-center">
              <Link
                href="/login"
                className="text-sm text-muted-foreground hover:text-primary flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" /> {t('backToLogin')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
